import { ArpMediaType } from '@arpeggi/shared'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import { useArpMedia, useUpdateArpMedia } from '../../../queries/arpMedia'
import {
  useAddFeaturedMedia,
  useDeleteFeaturedMedia,
  useFeaturedMedia
} from '../../../queries/featuredMedia'
import Button from '../../core/Button/Button'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Spacer } from '../../core/Components'
import { Flex } from '../../core/Flex'
import { LinkIcon } from '../../core/icons'
import Text from '../../core/Text/Text'
import { UserDetails } from '../components/common'

export const ArpDetails = () => {
  const params = useParams<{ id: string }>()
  const arpMediaId = parseInt(params.id)
  const arpMediaQuery = useArpMedia({ id: arpMediaId })
  const featuredMediaQuery = useFeaturedMedia({ arpMediaId, options: { retry: false } })
  const updateArpMedia = useUpdateArpMedia(arpMediaId)
  const addFeaturedMedia = useAddFeaturedMedia()
  const deleteFeaturedMedia = useDeleteFeaturedMedia()

  const handleAddToBlocklist = () => updateArpMedia.mutate({ isBlocklist: true })
  const handleRemoveFromBlocklist = () => updateArpMedia.mutate({ isBlocklist: false })

  const handelAddToFeaturedMedia = () => addFeaturedMedia.mutate({ arpMediaId })
  const handelRemoveFromFeaturedMedia = () => deleteFeaturedMedia.mutate({ arpMediaId })

  const handleAddToDefaultSampleList = () => updateArpMedia.mutate({ isDefaultSample: true })
  const handleRemoveFromDefaultSampleList = () => updateArpMedia.mutate({ isDefaultSample: false })

  const arpMedia = arpMediaQuery.data
  const isFeatured = !featuredMediaQuery.isLoading && !featuredMediaQuery.isError

  return (
    <>
      {arpMediaQuery.isLoading && <CenteredLoadingAnimation />}
      {!arpMediaQuery.isLoading && (
        <Flex direction="column" css={{ gap: '16px' }}>
          <Flex>
            <Text size="24">{arpMedia.metadata.title}</Text>
            <Spacer />
            <a href={`/${arpMedia.type}?token=${arpMedia.id}`} target="_blank">
              <Button big type="dark" css={{ gap: '8px' }}>
                Details Page
                <LinkIcon />
              </Button>
            </a>
          </Flex>
          <Flex direction="column" css={{ gap: '16px' }}>
            <Text size="16">
              Minted at: {format(new Date(arpMedia.mintedAt), 'MM/dd/yyy hh:ss aaa')}
            </Text>
            <Text size="16">Type: {arpMedia.type}</Text>
          </Flex>
          <Text size="20">User</Text>
          <UserDetails user={arpMedia.user} />
          {!featuredMediaQuery.isLoading && (
            <>
              <Text size="20">Actions</Text>
              {arpMedia.isBlocklist ? (
                <Flex direction="column" css={{ gap: '8px', width: 'max-content' }}>
                  <Text size="16">Is on the blocklist</Text>
                  <Button
                    type="dark"
                    big
                    css={{ backgroundColor: '$crimson05', width: 'auto' }}
                    onClick={handleRemoveFromBlocklist}
                    disabled={updateArpMedia.isLoading}
                  >
                    Remove from blocklist
                  </Button>
                </Flex>
              ) : (
                <Flex direction="column" css={{ gap: '8px', width: 'max-content' }}>
                  <Text size="16">Not on the blocklist</Text>
                  <Button
                    type="dark"
                    big
                    css={{ backgroundColor: '$green05' }}
                    onClick={handleAddToBlocklist}
                    disabled={updateArpMedia.isLoading}
                  >
                    Add to blocklist
                  </Button>
                </Flex>
              )}
              {isFeatured ? (
                <Flex direction="column" css={{ gap: '8px', width: 'max-content' }}>
                  <Text size="16">Is on the featured media list</Text>
                  <Button
                    type="dark"
                    big
                    css={{ backgroundColor: '$crimson05' }}
                    onClick={handelRemoveFromFeaturedMedia}
                    disabled={deleteFeaturedMedia.isLoading}
                  >
                    Remove from featured media list
                  </Button>
                </Flex>
              ) : (
                <Flex direction="column" css={{ gap: '8px', width: 'max-content' }}>
                  <Text size="16">Not on the featured media list</Text>
                  <Button
                    type="dark"
                    big
                    css={{ backgroundColor: '$green05' }}
                    onClick={handelAddToFeaturedMedia}
                    disabled={addFeaturedMedia.isLoading}
                  >
                    Add to featured media
                  </Button>
                </Flex>
              )}
              {arpMedia.type === ArpMediaType.SAMPLE &&
                (arpMedia.isDefaultSample ? (
                  <Flex direction="column" css={{ gap: '8px', width: 'max-content' }}>
                    <Text size="16">Is on the default sample list</Text>
                    <Button
                      type="dark"
                      big
                      css={{ backgroundColor: '$crimson05' }}
                      onClick={handleRemoveFromDefaultSampleList}
                      disabled={updateArpMedia.isLoading}
                    >
                      Remove from default sample list
                    </Button>
                  </Flex>
                ) : (
                  <Flex direction="column" css={{ gap: '8px', width: 'max-content' }}>
                    <Text size="16">Not on the default sample list</Text>
                    <Button
                      type="dark"
                      big
                      css={{ backgroundColor: '$green05' }}
                      onClick={handleAddToDefaultSampleList}
                      disabled={updateArpMedia.isLoading}
                    >
                      Add to default sample list
                    </Button>
                  </Flex>
                ))}
            </>
          )}
        </Flex>
      )}
    </>
  )
}
